// Common reference data
import msicCodes from '../data/common/msic-codes.json';
import { excludeCrsUnfilableMsic } from '@/shared/constants/crsMsic';
import nationalities from '../data/common/nationalities.json';
import malaysianStates from '../data/common/malaysian-states.json';
import cities from '../data/common/cities.json';
import postcodes from '../data/common/postcodes.json';
import races from '../data/common/races.json';
import genders from '../data/common/genders.json';
import companySubtypes from '../data/common/company-subtypes.json';
// IDPRO-specific reference data
import sharedTemplates from '../data/idpro/form-templates.json';
import idTypes from '../data/idpro/id-types.json';
// 2차 신분증 — CRS 는 1차와 **다른 코드표**(identificationTypeSecond: PASSPORT/OLD IC/NONE)를 쓴다.
import secondaryIdTypes from '../data/idpro/secondary-id-types.json';
import designations from '../data/idpro/designations.json';
import salutations from '../data/common/salutations.json';
import shareClasses from '../data/idpro/share-classes.json';
import companyRegistrationTypes from '../data/idpro/company-registration-types.json';
// 등록번호 표기(구/신) — CRS 는 Name Reservation 의 법인 프로모터에서만 묻는다(34167 OLD / 34168 NEW).
import registrationNoFormats from '../data/idpro/registration-no-formats.json';
// CRS 이름예약 경칭표(`salutationVO`) 31종 그대로. 공용 `salutations` 는 MR/DR 등
// CRS 에 없는 값까지 담고 있어, 고르면 전송 때 조용히 사라진다.
import salutationsCrs from '../data/idpro/salutations-crs.json';
// SSM 에 등록되지 않은 법인 멤버(외국법인·정부기관·공동보유) 분기용 CRS 코드표.
import memberCategories from '../data/idpro/member-categories.json';
import memberSubCategories from '../data/idpro/member-sub-categories.json';

// ===== Types (DI-based superset) =====

export interface FieldDef {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  uppercase?: boolean;
  maxlength?: number;
  /** CRS 가 글자수가 아니라 **단어수**로 제한하는 항목(Nature of Business = 200 words). */
  maxWords?: number;
  /** 필드 위에 붙이는 소제목. CRS 의 "Statement from EPF" 처럼 묶어 보여 줄 때. */
  groupTitle?: string;
  placeholder?: string;
  hint?: string;
  colSpan?: number;
  rows?: number;
  default?: any;
  min?: number;
  step?: number;
  pattern?: string;
  optional?: boolean;
  readonly?: boolean;
  options?: { value: string; label: string }[];
  dataSource?: string;
  dataSourceKey?: string; // For nested data structures (e.g., companySubtypes[companyType])
  datalistSource?: string;
  datalistFormat?: string;
  selectDataSource?: string;
  selectPlaceholder?: string;
  textPlaceholder?: string;
  textUppercase?: boolean;
  linkedCountryField?: string;
  countryValueForSelect?: string;
  fileUpload?: {
    accept: string;
    hint: string;
    label?: string;
    /** 본문에 값이 있을 때만 첨부칸을 연다(Clarification 3~6번). */
    showWhenFilled?: boolean;
    maxSizeMb?: number;
  };
  accept?: string;
  showWhen?: {
    field?: string;
    values?: string[];
    value?: string;
    notEmpty?: boolean;
    /** 체크박스가 꺼져 있을 때만 보이게 — "여권 없음" 을 체크하면 2차 신분증 칸을 감추는 식. */
    falsy?: boolean;
    all?: Array<{ field: string; values?: string[]; value?: string; notEmpty?: boolean; falsy?: boolean }>;
  };
  charCounter?: boolean;
  handler?: string;
  highlight?: boolean;
  disabled?: boolean;
  dynamicLabel?: {
    linkedField: string;
    dataSource: string;
    keys: Record<string, string>;
  };
  // Inline 액션 아이콘 (예: 클릭 시 entity 선택 모달 트리거).
  //   appendInnerIcon 만 정의되면 액션 명은 fieldId 로 대체. SectionRenderer 가 부모로 전달.
  appendInnerIcon?: string;
  appendInnerAction?: string;
  appendInnerIconColor?: string;
  // Date constraints
  maxDate?: string; // 'today' = cannot be in the future
}

export interface TemplateGroup {
  ref?: string;
  title?: string;
  style?: string;
  includeOptional?: boolean;
  overrides?: Record<string, Partial<FieldDef>>;
  excludeFields?: string[];
  fields?: FieldDef[];
}

export interface SectionDef {
  id: string;
  type: string;
  title: string;
  columns?: number;
  subtitle?: string;
  icon?: string;
  info?: string;
  required?: boolean;
  fields?: FieldDef[];
  minItems?: number;
  minItemsPublic?: number;
  addLabel?: string;
  itemLabel?: string;
  badgeClass?: string;
  borderColor?: string;
  themeColor?: string;
  fieldConfig?: FieldDef & { datalistFormat?: string };
  itemTemplate?: {
    headerFields?: FieldDef[];
    typeSelector?: FieldDef;
    groups?: TemplateGroup[];
    variants?: Record<string, { groups: TemplateGroup[] }>;
    commonGroups?: TemplateGroup[];
  };
  addressBlocks?: {
    id: string;
    title: string;
    templateRef: string;
    includeOptional?: boolean;
    defaultCountry?: string;
    lockCountry?: boolean;
    sameAs?: { target: string; label: string; default?: boolean };
    // "IF BUSINESS ADDRESS IS AVAILABLE" 토글(CRS 대응). 체크 해제 시 주소 없음.
    availableToggle?: { label: string; default?: boolean };
  }[];
  conditionalGroups?: {
    id: string;
    showWhen: { field: string; value: string };
    fields: FieldDef[];
  }[];
  extraGroups?: { id: string; fields: FieldDef[] }[];
  branchAddress?: {
    enabled: boolean;
    addLabel: string;
    templateRef: string;
    includeOptional: boolean;
    /** CRS 는 지점주소를 1건만 받는다. */
    maxItems?: number;
    maxItemsHint?: string;
  };
  maxItems?: number;
  /** attachment-list 섹션이 받는 확장자. */
  accept?: string;
  text?: string;
  /** declaration 섹션 하단 안내(예: CRS 에서 재확인 필요). */
  note?: string;
}

export interface RefDataItem {
  value?: string;
  label?: string;
  desc?: string;
  code?: string;
  [key: string]: any;
}

// ===== Shared Reference Data =====

/**
 * CRS 1차 신분증은 MYKAD / MYKAS / MYPR / MYTENTERA / FOREIGNER NATIONAL ID 다섯 개뿐이다
 * (2026-09-21 CRS Director·Member Details 실화면 확인).
 * Police ID 는 CRS 에서 **2차 전용** 코드라 1차로 고르면 `idType=null` 로 나가 조용히 유실된다.
 * T-Link 폼은 이미 걸러내고 있었는데 DI 폼만 그대로 노출하고 있었다 — 같은 기준으로 맞춘다.
 */
const crsPrimaryIdTypes = (idTypes as RefDataItem[]).filter((x) => x.crsPrimary !== false);

// CRS 에 신고할 수 없는 업종은 설립 폼 선택지에서 뺀다(사유는 crsMsic.ts).
const crsFilableMsicCodes = excludeCrsUnfilableMsic(msicCodes as RefDataItem[]);

const sharedRefData: Record<string, RefDataItem[]> = {
  msicCodes: msicCodes as RefDataItem[],
  msicCodesCrs: crsFilableMsicCodes,
  nationalities,
  malaysianStates,
  cities,
  postcodes,
  idTypes: crsPrimaryIdTypes,
  secondaryIdTypes,
  designations,
  salutations,
  races,
  genders,
  shareClasses,
  companyRegistrationTypes,
  registrationNoFormats,
  salutationsCrs,
  memberCategories,
  memberSubCategories,
};

// Company subtypes (structured by company type)
const companySubtypesMap = companySubtypes as Record<string, RefDataItem[]>;

// ===== Composable =====

export function useFormRenderer() {
  /**
   * Resolve template ref to field array (requires templates from form config)
   */
  function resolveTemplate(
    templates: Record<string, FieldDef[]>,
    ref: string,
    includeOptional?: boolean,
  ): FieldDef[] {
    const tpl = templates[ref];
    if (!tpl) return [];
    if (includeOptional === undefined) return [...tpl];
    return tpl.filter(f => includeOptional || !f.optional);
  }

  /**
   * Resolve a template group into fields
   */
  function resolveGroup(
    templates: Record<string, FieldDef[]>,
    group: TemplateGroup,
  ): FieldDef[] {
    if (group.fields) return group.fields;
    if (!group.ref) return [];
    let fields = resolveTemplate(templates, group.ref, group.includeOptional);
    // Exclude specified fields (e.g., name moved to headerFields)
    if (group.excludeFields?.length) {
      fields = fields.filter(f => !group.excludeFields!.includes(f.id));
    }
    if (group.overrides) {
      return fields.map(f => {
        const override = group.overrides![f.id];
        return override ? { ...f, ...override } : f;
      });
    }
    return fields;
  }

  /**
   * Get options from reference data
   */
  function getRefOptions(dataSourceKey: string): RefDataItem[] {
    return sharedRefData[dataSourceKey] || [];
  }

  /**
   * Get company subtypes by company type
   */
  function getCompanySubtypes(companyType: string): RefDataItem[] {
    return companySubtypesMap[companyType] || [];
  }

  /**
   * Get all company subtypes
   */
  function getAllCompanySubtypes(): RefDataItem[] {
    return companySubtypesMap.all || [];
  }

  return {
    sharedRefData,
    sharedTemplates: sharedTemplates as Record<string, FieldDef[]>,
    companySubtypesMap,
    resolveTemplate,
    resolveGroup,
    getRefOptions,
    getCompanySubtypes,
    getAllCompanySubtypes,
  };
}
